/**
 * MessageSources — provenance for a Bird answer: what it read, from where.
 *
 * A POPOVER, not an inline expander. The old collapsible pushed every message
 * below it down the thread the moment you asked "where did this come from?" —
 * which is exactly when you're mid-read. Floating the list keeps the answer
 * you're looking at exactly where it was.
 *
 * Sources are sorted Bower-first, then everything else: your own workspace is
 * the provenance that matters most, and it's the one a domain name can't
 * convey. Each row carries its origin as TEXT ("Bower", or the domain) — never
 * colour or icon alone.
 *
 * Connector attribution (Sheets / OneNote / Drive) isn't here yet: the wire
 * `GroundingSource` carries only `{title, url, domain, index}`, so a connector
 * result is indistinguishable from a web result. Labelling one "Sheets" today
 * would mean guessing from the domain — that needs a backend field, not a
 * frontend heuristic.
 */

import {
  Box,
  Button,
  HStack,
  Image,
  Link as ChakraLink,
  Skeleton,
  SkeletonText,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useMemo } from 'react';

import type { GroundingSource } from '../../../services/agentChat.service';
import { getDisplayTitle } from '../../../utils/artifactUtils';
import { BowerIcon } from '../../ui/BowerIcon';
import { MaterialIcon } from '../../ui/MaterialIcon';
import { ResponsivePopover } from '../../ui/ResponsivePopover';
import { ANALYTICS_EVENTS } from '../../../constants/analytics';
import { safeAnalytics } from '../../../utils/analytics';
import { getFaviconUrl, getSafeDomain, isInternalUrl, useUrlMetadata } from './sourceUrls';
import type { InternalLinkPolicy } from './EntityLink';

/** One source row — fetches its own metadata once the panel is open. */
function SourceItem({
  source,
  internalLinkPolicy,
}: {
  readonly source: GroundingSource;
  readonly internalLinkPolicy: InternalLinkPolicy;
}) {
  const internal = isInternalUrl(source.url);
  const { metadata, isLoading } = useUrlMetadata(source.url, true);

  const linkUrl = metadata?.url || source.url;
  const displayTitle = metadata?.title || source.title;
  const displayDomain = metadata?.domain || source.domain || getSafeDomain(source.url, '');
  const faviconUrl = getFaviconUrl(linkUrl, metadata?.favicon);
  // Origin as words. A favicon alone tells a sighted user where a link goes
  // and tells a screen-reader user nothing.
  const originLabel = internal ? 'Bower' : displayDomain;

  const faviconOrFallback = faviconUrl ? (
    <Image
      src={faviconUrl}
      alt=""
      w={4}
      h={4}
      loading="lazy"
      decoding="async"
      fallback={<MaterialIcon name="language" boxSize={3} color="text.muted" />}
    />
  ) : (
    <MaterialIcon name="language" boxSize={3} color="text.muted" />
  );

  const externalIconContent = isLoading ? (
    <Skeleton w={4} h={4} borderRadius="sm" />
  ) : (
    faviconOrFallback
  );

  const iconContent = internal ? <BowerIcon name="note" size="12px" /> : externalIconContent;
  const linked = !(internal && internalLinkPolicy === 'label');

  const row = (
    <HStack
      spacing={2.5}
      align="center"
      py={1.5}
      px={1.5}
      borderRadius="md"
      _hover={internalLinkPolicy === 'label' && internal ? undefined : { bg: 'bg.hover' }}
    >
      {/* NOT a `Badge` — a fixed 16x16 disc, the same object `CitationLabel`
          in `BirdMessage` draws inline, and it has to match that one index for
          index. See the note there for why a disc is not a lozenge. */}
      <Box
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        w={4}
        h={4}
        fontSize="2xs"
        fontWeight="semibold"
        color="brand.onSurface"
        bg="brand.surface"
        borderRadius="full"
        flexShrink={0}
      >
        {source.index}
      </Box>
      <Box
        flexShrink={0}
        w={5}
        h={5}
        borderRadius="sm"
        bg="bg.muted"
        display="flex"
        alignItems="center"
        justifyContent="center"
        overflow="hidden"
      >
        {iconContent}
      </Box>
      <VStack align="flex-start" spacing={0} flex={1} minW={0}>
        {isLoading && !internal ? (
          <SkeletonText noOfLines={1} skeletonHeight={2} w="120px" />
        ) : (
          // The only underline in the row; matches the global link hairline.
          <Text
            fontSize="xs"
            fontWeight="medium"
            color="text.primary"
            noOfLines={1}
            textDecoration={linked ? 'underline' : undefined}
            textDecorationColor="color-mix(in srgb, currentColor 50%, transparent)"
            textUnderlineOffset="3px"
            textDecorationThickness="1px"
            _groupHover={{ textDecorationColor: 'currentColor' }}
          >
            {getDisplayTitle(displayTitle)}
          </Text>
        )}
        {/* `fontWeight="normal"` is explicit: the whole row is wrapped in an
              anchor and the Link baseStyle sets `medium` on it, so without
              this the muted origin line inherits the same weight as the title
              above and the hierarchy collapses. */}
        {originLabel && (
          <Text fontSize="2xs" fontWeight="normal" color="text.muted" noOfLines={1}>
            {originLabel}
          </Text>
        )}
      </VStack>
    </HStack>
  );

  if (!linked) {
    return (
      <Box role="note" borderRadius="md">
        {row}
      </Box>
    );
  }

  return (
    <ChakraLink
      href={linkUrl}
      isExternal={!internal}
      // `data-group`, not `role="group"`: a role would replace the link role.
      data-group=""
      // Opts out of the global anchor rule (theme/styles.ts), which outscores
      // a style prop: on a whole-row link its underline reached the number
      // disc and the origin line too. The title Text re-draws it on its own.
      data-link-plain=""
      textDecoration="none"
      _hover={{ textDecoration: 'none' }}
      borderRadius="md"
      _focusVisible={{ boxShadow: 'outline' }}
    >
      {row}
    </ChakraLink>
  );
}

export function MessageSources({
  sources,
  internalLinkPolicy = 'navigate',
}: {
  readonly sources: GroundingSource[];
  readonly internalLinkPolicy?: InternalLinkPolicy;
}) {
  // Bower first, then everything else, each group keeping its citation order
  // so the numbers still climb within a group.
  const ordered = useMemo(() => {
    const internal = sources.filter((s) => isInternalUrl(s.url));
    const external = sources.filter((s) => !isInternalUrl(s.url));
    return [...internal, ...external];
  }, [sources]);

  const count = ordered.length;
  const label = `${count} ${count === 1 ? 'source' : 'sources'}`;

  return (
    <Box mt={2}>
      <ResponsivePopover
        onOpened={() =>
          safeAnalytics.track(ANALYTICS_EVENTS.CHAT_SOURCES_OPENED, { source_count: count })
        }
        title={
          <>
            <MaterialIcon name="menu_book" boxSize={3.5} color="brand.onSurface" />
            Sources
          </>
        }
        placement="top-start"
        trigger={
          <Button
            aria-label={`Show ${label}`}
            size="xs"
            variant="ghost"
            bg="bg.muted"
            // text.secondary on bg.muted is 4.19:1 — under AA at this size.
            color="text.onMutedBg"
            fontWeight="medium"
            borderRadius="lg"
            px={2.5}
            h="28px"
            rightIcon={<MaterialIcon name="chevron_right" boxSize={3.5} />}
            _hover={{ bg: 'bg.hover' }}
          >
            {label}
          </Button>
        }
      >
        <VStack spacing={0.5} align="stretch">
          {ordered.map((source) => (
            <SourceItem key={source.url} source={source} internalLinkPolicy={internalLinkPolicy} />
          ))}
        </VStack>
      </ResponsivePopover>
    </Box>
  );
}
